import React, { useEffect, useState } from "react";
import { startRegistration } from "@simplewebauthn/browser";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { authApi } from "../../api/auth";
import { marketingApi } from "../../api/marketing";
import client from "../../api/client";
import { formatApiError } from "../../lib/errors";
import { PASSWORD_HINT_KEY, PASSWORD_LIMITS, validatePassword, confirmError } from "../../lib/password";
import PasswordField from "../auth/PasswordField";

export default function AccountSettingsPage() {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation("dashboard");
  const [name, setName] = useState(user?.name || "");
  const [profile, setProfile] = useState(null);
  const [passkeys, setPasskeys] = useState([]);
  const [marketingConsent, setMarketingConsent] = useState(null); // null = loading
  const [savingMarketing, setSavingMarketing] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [addingPasskey, setAddingPasskey] = useState(false);

  const refreshAll = async () => {
    try {
      const [profResp, passResp, prefsResp] = await Promise.all([
        client.get("/api/account/profile"),
        authApi.passkeyList(),
        marketingApi.getPreferences(),
      ]);
      setProfile(profResp.data);
      setPasskeys(passResp.data);
      setMarketingConsent(Boolean(prefsResp.data?.marketing_consent));
    } catch (err) {
      // non-fatal — page still renders, just without conditional sections
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setMessage(""); setError("");
    try { await client.patch("/api/account/profile", { name }); setMessage(t("settings.profileUpdated")); }
    catch (err) { setError(formatApiError(err, t("settings.updateFailed"))); }
  };

  const newMismatch = confirmError(newPassword, confirmPassword) !== null;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage(""); setError("");
    // Changing a password creates one, so it gets the same confirmation as
    // signup and reset. A typo here is worse than either: the server bumps
    // token_version (account.py:59), so it signs you out of every other device
    // and hands you a value you never knew.
    if (confirmError(newPassword, confirmPassword) || newPassword !== confirmPassword) {
      return;
    }
    const pwdError = validatePassword(newPassword);
    if (pwdError) {
      setError(t(pwdError, PASSWORD_LIMITS));
      return;
    }
    try {
      await client.post("/api/account/change-password", { current_password: currentPassword, new_password: newPassword });
      setMessage(t("settings.passwordChanged")); setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) { setError(formatApiError(err, t("settings.passwordChangeFailed"))); }
  };

  const handleAddPasskey = async () => {
    setMessage(""); setError("");
    setAddingPasskey(true);
    try {
      const startResp = await authApi.passkeyRegisterStart();
      const { challenge_id, options } = startResp.data;
      const credential = await startRegistration(options);
      await authApi.passkeyRegisterComplete({ challenge_id, credential });
      setMessage(t("settings.passkeyAdded"));
      await refreshAll();
    } catch (err) {
      setError(formatApiError(err, err.message || t("settings.passkeyAddFailed")));
    } finally {
      setAddingPasskey(false);
    }
  };

  const handleDeletePasskey = async (id) => {
    if (!window.confirm(t("settings.passkeyDeleteConfirm"))) return;
    setMessage(""); setError("");
    try {
      await authApi.deletePasskey(id);
      setMessage(t("settings.passkeyDeleted"));
      await refreshAll();
    } catch (err) {
      setError(formatApiError(err, t("settings.passkeyDeleteFailed")));
    }
  };

  const handleMarketingToggle = async (e) => {
    const next = e.target.checked;
    // Optimistic flip: instant UI feedback, rollback if the server rejects.
    setMessage(""); setError("");
    setMarketingConsent(next);
    setSavingMarketing(true);
    try {
      await marketingApi.updatePreferences(next);
      setMessage(next ? t("settings.marketingOn") : t("settings.marketingOff"));
    } catch (err) {
      setMarketingConsent(!next);
      setError(formatApiError(err, t("settings.marketingUpdateFailed")));
    } finally {
      setSavingMarketing(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmMsg = t("settings.deleteConfirm");
    if (!window.confirm(confirmMsg)) return;
    try { await client.delete("/api/account/"); await logout(); window.location.href = "/"; }
    catch (err) { setError(formatApiError(err, t("settings.deleteFailed"))); }
  };

  return (
    <div className="space-y-8">
      <h1 className="font-clash text-2xl font-bold text-white">{t("settings.title")}</h1>
      {message && <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-4 py-3 rounded text-sm">{message}</div>}
      {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded text-sm">{error}</div>}

      <div className="feature-card rounded-lg p-6">
        <h2 className="text-white font-semibold mb-4">{t("settings.profileHeading")}</h2>
        <form onSubmit={handleUpdateProfile} className="space-y-4 max-w-md">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("settings.name")}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-white focus:border-[#FF5500] focus:outline-none" />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("settings.email")}</label>
            <input type="email" value={user?.email || ""} disabled
              className="w-full bg-[#1a1a1e] border border-white/10 rounded px-4 py-3 text-[#A1A1AA]" />
          </div>
          <button type="submit" className="btn-primary px-6 py-2 rounded text-sm font-semibold">{t("settings.save")}</button>
        </form>
      </div>

      {profile?.has_password && (
        <div className="feature-card rounded-lg p-6">
          <h2 className="text-white font-semibold mb-4">{t("settings.changePassword")}</h2>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("settings.currentPassword")}</label>
              <PasswordField value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password" required />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("settings.newPassword")}</label>
              <PasswordField value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password" required minLength={PASSWORD_LIMITS.min} />
              <p className="text-xs text-[#A1A1AA] mt-1">{t(PASSWORD_HINT_KEY, PASSWORD_LIMITS)}</p>
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-wider text-[#A1A1AA] block mb-2">{t("auth:password.confirmLabel")}</label>
              <PasswordField value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password" invalid={!!newMismatch} required minLength={PASSWORD_LIMITS.min} />
              {newMismatch && <p className="text-xs text-red-400 mt-1">{t("auth:password.mismatch")}</p>}
            </div>
            <button type="submit" className="btn-primary px-6 py-2 rounded text-sm font-semibold">{t("settings.changePassword")}</button>
          </form>
        </div>
      )}

      <div className="feature-card rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold">{t("settings.passkeysHeading")}</h2>
          <button
            onClick={handleAddPasskey}
            disabled={addingPasskey}
            className="text-sm text-[#FF5500] hover:text-[#FF7733] disabled:text-[#A1A1AA]"
          >
            {addingPasskey ? t("settings.passkeyWaiting") : t("settings.passkeyAdd")}
          </button>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-4">
          {t("settings.passkeyDesc")}
        </p>
        {passkeys.length === 0 ? (
          <p className="text-sm text-[#A1A1AA]">{t("settings.passkeyEmpty")}</p>
        ) : (
          <ul className="divide-y divide-white/5">
            {passkeys.map((p) => (
              <li key={p.id} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-white text-sm">{p.name}</p>
                  <p className="text-xs text-[#A1A1AA]">{t("settings.passkeyAddedOn", { date: new Date(p.created_at).toLocaleDateString(i18n.language) })}</p>
                </div>
                <button
                  onClick={() => handleDeletePasskey(p.id)}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  {t("settings.passkeyDelete")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="feature-card rounded-lg p-6">
        <h2 className="text-white font-semibold mb-2">{t("settings.emailPrefsHeading")}</h2>
        <p className="text-sm text-[#A1A1AA] mb-4">
          {t("settings.emailPrefsDesc")}
        </p>
        <label className="flex items-start gap-3 cursor-pointer select-none max-w-md">
          <input
            type="checkbox"
            checked={marketingConsent === true}
            disabled={marketingConsent === null || savingMarketing}
            onChange={handleMarketingToggle}
            className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer accent-[#FF5500] disabled:cursor-wait"
          />
          <span className="text-sm leading-relaxed text-white">
            {t("settings.marketingLabel")}
            <span className="block text-xs text-[#A1A1AA] mt-1">
              {t("settings.marketingFinePrint")}
            </span>
          </span>
        </label>
      </div>

      <div className="feature-card rounded-lg p-6 border border-red-500/20">
        <h2 className="text-red-400 font-semibold mb-4">{t("settings.dangerZone")}</h2>
        <p className="text-sm text-[#A1A1AA] mb-2">{t("settings.dangerIntro")}</p>
        <ul className="text-sm text-[#A1A1AA] mb-4 list-disc pl-5 space-y-1">
          <li>{t("settings.dangerBullet1")}</li>
          <li>{t("settings.dangerBullet2Pre")}<span className="text-white">{t("settings.dangerBullet2Emphasis")}</span>{t("settings.dangerBullet2Post")}</li>
        </ul>
        <button onClick={handleDeleteAccount} className="px-6 py-2 rounded border border-red-500/30 text-red-400 text-sm hover:bg-red-500/10 transition-colors">{t("settings.deleteAccount")}</button>
      </div>
    </div>
  );
}
